import Fuse from 'fuse.js';
import {useContext, useEffect, useRef} from 'react';
import {KEY_DS_DANH_MUC_TIEP_DON} from '../../utils/Constant';
import {decodeHEXString} from '../../utils/FormatUtils';
import {getIndexedDBItem} from '../../utils/IndexedDB';
import {IDanhSachDanhMuc} from '../models/TiepDonModel';
import {DEFAULT_CODE_SELECT_TIEPDON} from "../constants/constants";
import {AppContext} from "../../appContext/AppContext";
import {checkAddress, getSubDistrictByProvinceId} from "../util/FunctionUtil";

interface IndexedItem {
  original: any;
  districtNorm: string;
  provinceNorm: string;
  wardNorm: string;
}
let searchIndexCache: IndexedItem[] | null = null;
let lastOptionsCache: any[] | null = null;
const useReadQRCode = () => {
	const categoryRef = useRef<any>(null);
  const {setIsLoading} = useContext(AppContext);

	useEffect(() => {
		getDanhMuc();
	}, []);

	const getDanhMuc = async () => {
    try {
      setIsLoading(true);
      const danhMuc: IDanhSachDanhMuc = await getIndexedDBItem(KEY_DS_DANH_MUC_TIEP_DON);
      categoryRef.current = danhMuc;
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false);
    }
	};

	const convertNgaySinh = (value: string = '') => {
		if (value.includes('/')) {
			const [ngaySinh, thangSinh, namSinh] = value.split('/');
			return {
				ngaySinh,
				thangSinh,
				namSinh,
			};
		}

		let ngaySinh = null,
			thangSinh = null,
			namSinh = null;

		if (value.length === 8) {
			ngaySinh = value.slice(0, 2);
			thangSinh = value.slice(2, 4);
			namSinh = value.slice(4);
		}

		if (value.length === 6) {
			thangSinh = value.slice(0, 2);
			namSinh = value.slice(2);
		}

		if (value.length === 4) {
			namSinh = value;
		}

		return {
			ngaySinh,
			thangSinh,
			namSinh,
		};
	};

	const getCategoryOptionFromName = (value: string, options: any[], keys?: string[]) => {
		const fuse = new Fuse(options || [], {
			includeScore: true,
			includeMatches: true,
			useExtendedSearch: true,
			ignoreLocation: true,
			isCaseSensitive: true,
			threshold: 0.1,
			keys: ['name', ...(keys || [])],
		});

		const result = fuse
			.search(`"${value}"`, {
				limit: 1,
			})
			.map(record => record.item);
		return result[0];
	};

	const createSearchIndex = (options: any[]): IndexedItem[] => {
		if (!options || !Array.isArray(options)) return [];

		const normalizeStr = (str: string) => str.toLowerCase();

		return options.map(item => ({
			original: item,
			districtNorm: normalizeStr(item.relatedDistrictName || ''),
			provinceNorm: normalizeStr(item.relatedProvinceName || ''),
			wardNorm: normalizeStr(item.subdistrictRelatedName || '')
		}));
	};

	const getSubDistrict = (
		district: string,
		province: string,
		ward: string,
		options: any[]
	) => {
		if (!options || !Array.isArray(options)) return {};

		// Cache index nếu chưa có hoặc options thay đổi
		if (!searchIndexCache || lastOptionsCache !== options) {
			searchIndexCache = createSearchIndex(options);
			lastOptionsCache = options;
		}

		const districtLower = district.toLowerCase();
		const provinceLower = province.toLowerCase();
		const wardLower = ward.toLowerCase();

		const found = searchIndexCache.find(indexed =>
			(indexed.districtNorm.includes(districtLower) || districtLower.includes(indexed.districtNorm)) &&
			(indexed.provinceNorm.includes(provinceLower) || provinceLower.includes(indexed.provinceNorm)) &&
			(indexed.wardNorm.includes(wardLower) || wardLower.includes(indexed.wardNorm))
		);

		return found?.original || {};
	};

	const getAddressInfo = async (addressInput: string, isLookup?: boolean) => {
    let addressInfo: {
      town: string;
      ward: string;
      district: string;
      province: string;
      provinceOption: {} | null;
      districtOption: {} | null;
      wardOption: {} | null;
      administrativeUnit?: string;
    };
    const ADDRESS_SEPARATOR = /,/;
    const addressArray = checkAddress(addressInput, isLookup).split(ADDRESS_SEPARATOR);
    const OLD_ADDRESS_LENGTH = 4;

    if (!categoryRef?.current) {
      await getDanhMuc();
    }

    console.log("addressArray: ", addressArray);
    if (addressArray.length >= OLD_ADDRESS_LENGTH) {
      const [town, ward, district, province] = addressArray;
        
      const address = getSubDistrict(district, province, ward, categoryRef?.current?.subDistrictsAllRelated) || {};
      console.log("QR address found: ", address)
      const provinceOption = {
        name: address.provinceName,
        code: address.provinceCode,
        id: address.provinceId,
      }
      const districtOption = {
        name: address.relatedDistrictName,
        code: address.relatedDistrictCode,
        id: address.relatedDistrictId,
      }
      const wardOption = getCategoryOptionFromName(
        address.subDistrictId,
        categoryRef?.current?.subDistricts || [],
        ["id"]
      );
      addressInfo = {
        administrativeUnit: wardOption?.shortName || "",
        town: town?.includes('.') ? town.replace('.', ',') : town,
        ward,
        district,
        province,
        provinceOption: (address.provinceName || address.provinceCode) ? provinceOption : null,
        districtOption,
        wardOption: (address.subDistrictName || address.subDistrictCode) ? wardOption : null,
      };
    } else {
      const [town, ward, province] = addressArray;
      const provinceOption = getCategoryOptionFromName(province, categoryRef?.current?.provinces);
      const wardOption = getCategoryOptionFromName(ward, categoryRef?.current?.subDistricts);

      addressInfo = {
        town,
        ward,
        district: "",
        province,
        provinceOption,
        districtOption: {},
        wardOption,
      };
    }

		return {...addressInfo, insAddress: addressInput};
	};

	const getBirthDateInfo = (birthDate: string) => {
		const { ngaySinh, thangSinh, namSinh } = convertNgaySinh(birthDate);

		return {
			ngaySinh,
			thangSinh,
			namSinh,
		};
	};

	const getGenderInfo = async (genderName: string) => {
    if (!categoryRef?.current) {
      await getDanhMuc();
    }
		return getCategoryOptionFromName(genderName, categoryRef?.current?.listGioiTinh);
	};

	const readCanCuocQRCode = async (qrcode: string) => {
		const rawValue = qrcode.split(/\|/);
		const [soCccd, soCmnd, hoTen, ngayThangNamSinh, gioiTinh, diaChi = '', ngayLamCanCuoc,,hoTenBo, hoTenMe,] = rawValue;

    const addressInfo: any = await getAddressInfo(diaChi);
		const genderOption = getCategoryOptionFromName(gioiTinh, categoryRef?.current?.listGioiTinh);

    if (addressInfo?.provinceOption?.id) {
      addressInfo.listSubDistrictFiltered = getSubDistrictByProvinceId(
        addressInfo?.provinceOption?.id || "",
        categoryRef?.current?.subDistricts || []
      ) || [];
    }

		return {
      communeId: null,
      communeName: null,
      communeCode: null,
      districtCode: null,
      districtId: null,
      districtName: null,
      provinceCode: null,
      provinceId: null,
      provinceName: null,
      ethnicity: categoryRef?.current?.listDanToc?.find(
        (item: any) => item?.code === DEFAULT_CODE_SELECT_TIEPDON.DAN_TOC_KINH
      ),
			hoTen: hoTen?.toUpperCase(),
			soCccd,
			soCmnd,
			ngayLamCanCuoc,
			diaChi,
			gioiTinh,
			genderOption,
			...getBirthDateInfo(ngayThangNamSinh),
			...addressInfo,
      hoTenBo,
      hoTenMe,
    };
	};

	const readBHYTQRCode = async (qrcode: string) => {
		const rawValue = qrcode.split(/\|/);
		const MA_THE_BHYT_CU_LENGTH = 15;
		const maTheBHYT = rawValue[0];
		let rawValueObject: {
			maSoBHYT: string;
			hoTenEncode: string;
			ngayThangNamSinh: string;
			maGioiTinh: string;
			maNoiKCB: string;
			maDoiTuong: string;
      maMucHuong: string;
			giaTriSuDungTuNgay: string;
			thoiDiem5NamLienTuc: string;
			diaChiEncode: string;
		};

		if (maTheBHYT.length === MA_THE_BHYT_CU_LENGTH) {
			const [
				maSoBHYT,
				hoTenEncode,
				ngayThangNamSinh,
				maGioiTinh,
				diaChiEncode,
				maNoiKCB,
				giaTriSuDungTuNgay,
				,
				,
				,
				,
				maDoiTuong,
				thoiDiem5NamLienTuc,
				,
				,
			] = rawValue;

			rawValueObject = {
				maSoBHYT,
				hoTenEncode,
				ngayThangNamSinh,
				maGioiTinh,
				maNoiKCB,
				maDoiTuong,
        maMucHuong: "",
				giaTriSuDungTuNgay,
				thoiDiem5NamLienTuc,
				diaChiEncode,
			};
		} else {
			const [
				maSoBHYT,
				hoTenEncode,
				ngayThangNamSinh,
				maGioiTinh,
				,
				maNoiKCB,
				giaTriSuDungTuNgay,
				,
				,
				,
				,
				maMucHuong,
				thoiDiem5NamLienTuc,
				,
				,
        diaChiEncode,
				,
			] = rawValue;

			rawValueObject = {
				maSoBHYT,
				hoTenEncode,
				ngayThangNamSinh,
				maGioiTinh,
				maNoiKCB,
				maDoiTuong: "",
        maMucHuong,
				giaTriSuDungTuNgay,
				thoiDiem5NamLienTuc,
				diaChiEncode,
			};
		}

		const hoTen = decodeHEXString(rawValueObject.hoTenEncode);
		const diaChi = rawValueObject.diaChiEncode
			? decodeHEXString(rawValueObject.diaChiEncode)
			: '';
		const GENDER_MAPPER: Record<string, string> = {
			1: 'Nam',
			2: 'Nữ',
			3: 'Không xác định',
		};
		const genderOption = await getGenderInfo(GENDER_MAPPER[rawValueObject.maGioiTinh]);
    const addressInfo: any = await getAddressInfo(diaChi);

		return {
			maSoBHYT: rawValueObject.maSoBHYT,
			hoTen,
			genderOption,
			maNoiKCB: rawValueObject.maNoiKCB,
			giaTriSuDungTuNgay: rawValueObject.giaTriSuDungTuNgay,
      gtTheTu: rawValueObject.giaTriSuDungTuNgay,
			maDoiTuong: rawValueObject.maDoiTuong,
			maMucHuong: rawValueObject.maMucHuong,
			thoiDiem5NamLienTuc: rawValueObject.thoiDiem5NamLienTuc,
			...getBirthDateInfo(rawValueObject.ngayThangNamSinh),
			...addressInfo,
		};
	};

	return {
		readCanCuocQRCode,
		readBHYTQRCode,
		getAddressInfo,
		getBirthDateInfo,
		getGenderInfo,
    getCategoryOptionFromName,
	};
};

export default useReadQRCode;
